// Rewarded-ad lifecycle (design §8.1 激励视频 / §8.2 广告加载策略, TECH_SPEC §4bis.17).
//
// Google H5 Games Ads (the Ad Placement API). Three properties matter more than
// the API surface:
//
// 1. **It is a flag.** The server decides (env `ADS_ENABLED` + `ADS_CLIENT`);
//    while the H5 Games Ads application is under review the answer is `false`
//    and this module does *nothing*: no script tag, no request, no bytes, no
//    revive button. Production today is exactly the game without ads.
// 2. **It never enters the 200 KB core bundle's load path.** §8.2: the SDK is
//    lazy-loaded only after the session's first `game_end`, so the first run —
//    the only one that decides whether anybody plays a second — is never slowed
//    by an ad network.
// 3. **Not ready = not offered.** §4.6: 广告未就绪，不显示复活按钮，死亡文案照常.
//    Every failure path in here therefore ends in "no offer", never in a broken
//    button or a stalled death screen.
//
// The Google globals are typed minimally and defensively: the script may never
// exist (disabled, blocked, offline), so nothing here may assume it does.

import { track as trackEvent, type EventProps } from '../analytics';
import type { AdsConfig } from '../types';

export type { AdsConfig };

export const ADS_DISABLED: AdsConfig = Object.freeze({
  enabled: false,
  client: null,
  testMode: false,
});

/** Result of one rewarded placement; only `viewed` earns the revive. */
export type RewardOutcome = 'viewed' | 'dismissed' | 'error';

const SDK_ORIGIN = 'https://pagead2.googlesyndication.com';
const SDK_PATH = '/pagead/js/adsbygoogle.js';
/** Design §8.2: one rewarded break per run, so a slow hint is the honest one. */
const FREQUENCY_HINT = '30s';
const PLACEMENT_NAME = 'revive';

// Watchdogs. Every promise this module hands out has to settle, because the
// caller is a frozen death frame with no UI on it: an SDK that goes quiet must
// end the run, not strand the player. Both are far longer than the healthy
// case — a preloaded break fills in seconds, and a rewarded video runs ~30 s —
// so they only ever fire on a genuine hang.
const PREPARE_TIMEOUT_MS = 30_000;
const SHOW_TIMEOUT_MS = 120_000;

// --- the bits of the Ad Placement API we use -------------------------------

type ShowAdFn = () => void;

export interface PlacementInfo {
  breakType?: string;
  breakName?: string;
  breakFormat?: string;
  breakStatus?: string;
}

export interface RewardedPlacement {
  type: 'reward';
  name: string;
  beforeAd?: () => void;
  afterAd?: () => void;
  beforeReward?: (showAdFn: ShowAdFn) => void;
  adDismissed?: () => void;
  adViewed?: () => void;
  adBreakDone?: (info: PlacementInfo) => void;
}

export interface AdConfigRequest {
  preloadAdBreaks?: 'on' | 'off';
  sound?: 'on' | 'off';
  onReady?: () => void;
}

interface AdWindow {
  adsbygoogle?: unknown[];
  adBreak?: (placement: RewardedPlacement) => void;
  adConfig?: (config: AdConfigRequest) => void;
}

/** Everything that touches the page, injected so tests need no DOM or network. */
export interface AdsRuntime {
  loadScript(src: string, attrs: Record<string, string>): Promise<void>;
  adConfig(config: AdConfigRequest): void;
  adBreak(placement: RewardedPlacement): void;
}

export function browserRuntime(): AdsRuntime {
  const w = (): AdWindow => window as unknown as AdWindow;
  return {
    loadScript(src, attrs) {
      return new Promise((resolve, reject) => {
        // Google's snippet: the queue and the two function shims must exist
        // before the tag, so calls made while it downloads are replayed rather
        // than thrown away.
        const win = w();
        const queue = (win.adsbygoogle ??= []);
        const push = (o: unknown): void => {
          queue.push(o);
        };
        win.adBreak ??= push as AdWindow['adBreak'];
        win.adConfig ??= push as AdWindow['adConfig'];

        const el = document.createElement('script');
        el.async = true;
        el.src = src;
        el.crossOrigin = 'anonymous';
        for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
        el.addEventListener('load', () => resolve());
        el.addEventListener('error', () => reject(new Error('ad_sdk_load_failed')));
        document.head.append(el);
      });
    },
    adConfig(config) {
      w().adConfig?.(config);
    },
    adBreak(placement) {
      w().adBreak?.(placement);
    },
  };
}

/** Script URL for a publisher id — exported so a test can assert it exactly. */
export function sdkUrl(client: string): string {
  return `${SDK_ORIGIN}${SDK_PATH}?client=${encodeURIComponent(client)}`;
}

type SdkState = 'off' | 'idle' | 'loading' | 'loaded' | 'failed';
type SlotState = 'none' | 'preparing' | 'ready' | 'showing';

/** Arms a one-shot timer; the returned function cancels it. */
export type Scheduler = (fn: () => void, ms: number) => () => void;

const timerScheduler: Scheduler = (fn, ms) => {
  const id = setTimeout(fn, ms);
  return () => clearTimeout(id);
};

export interface AdManagerOptions {
  runtime?: AdsRuntime;
  emit?: (name: AdEventName, props?: EventProps) => void;
  /** Injected in tests so the watchdogs are deterministic. */
  schedule?: Scheduler;
}

export type AdEventName =
  | 'ad_ready'
  | 'ad_fail'
  | 'ad_revive_shown'
  | 'ad_revive_viewed'
  | 'ad_revive_dismissed';

/**
 * One per page. Lifecycle:
 *
 *   noteGameEnd()  → (first call only) lazy-load the SDK + adConfig
 *   prepareRewarded() at every run start → adBreak({type:'reward'}) 预取
 *   beforeReward(showAdFn) → rewardedReady() === true → the offer may be shown
 *   showRewarded() → 'viewed' (revive) | 'dismissed' | 'error' (no revive)
 */
export class AdManager {
  private sdk: SdkState;
  private slot: SlotState = 'none';
  private showAd: ShowAdFn | null = null;
  private settle: ((outcome: RewardOutcome) => void) | null = null;
  /** prepareRewarded() called before the SDK finished loading. */
  private wantSlot = false;
  /**
   * Generation of the current placement. Google's callbacks are closures over
   * one `adBreak` call and can arrive late — after we already gave up on that
   * break. Without this, a straggler `adBreakDone` from the previous run would
   * null out the ad the *current* run just loaded.
   */
  private epoch = 0;
  private cancelWatchdog: (() => void) | null = null;

  private readonly runtime: AdsRuntime;
  private readonly emit: (name: AdEventName, props?: EventProps) => void;
  private readonly schedule: Scheduler;

  constructor(
    readonly config: AdsConfig,
    opts: AdManagerOptions = {},
  ) {
    this.sdk = config.enabled && config.client ? 'idle' : 'off';
    this.runtime = opts.runtime ?? browserRuntime();
    this.emit = opts.emit ?? ((name, props) => trackEvent(name, props ?? {}));
    this.schedule = opts.schedule ?? timerScheduler;
  }

  get enabled(): boolean {
    return this.sdk !== 'off';
  }

  /** True only when a rewarded ad is loaded and can be shown right now. */
  rewardedReady(): boolean {
    return this.slot === 'ready' && this.showAd !== null;
  }

  /**
   * Design §8.2: 首局 game_end 之后才懒加载. Idempotent — later game_ends are
   * no-ops, and a failed load is never retried (a player who can't reach the ad
   * network shouldn't pay for a retry on every death).
   */
  noteGameEnd(): void {
    if (this.sdk !== 'idle') return;
    const client = this.config.client;
    if (!client) return;
    this.sdk = 'loading';
    const attrs: Record<string, string> = { 'data-ad-frequency-hint': FREQUENCY_HINT };
    if (this.config.testMode) attrs['data-adbreak-test'] = 'on';
    this.runtime.loadScript(sdkUrl(client), attrs).then(
      () => {
        this.sdk = 'loaded';
        this.runtime.adConfig({
          preloadAdBreaks: 'on',
          sound: 'on',
          onReady: () => {
            if (this.wantSlot) this.requestSlot();
          },
        });
        if (this.wantSlot) this.requestSlot();
      },
      (err: unknown) => {
        this.sdk = 'failed';
        this.wantSlot = false;
        this.emit('ad_fail', { reason: 'script', detail: describe(err) });
      },
    );
  }

  /**
   * Design §8.2: 激励视频在每局开跑时预取. Safe to call on every run start —
   * before the SDK exists it only records the intent, and an already-loaded ad
   * is kept rather than re-requested.
   */
  prepareRewarded(): void {
    if (!this.enabled) return;
    if (this.slot !== 'none') return; // preparing / ready / showing: leave it be
    if (this.sdk !== 'loaded') {
      this.wantSlot = true;
      return;
    }
    this.requestSlot();
  }

  private requestSlot(): void {
    if (this.slot !== 'none') return;
    this.wantSlot = false;
    this.slot = 'preparing';
    const gen = ++this.epoch;
    const current = (): boolean => gen === this.epoch;
    let rewarded = false;
    try {
      this.runtime.adBreak({
        type: 'reward',
        name: PLACEMENT_NAME,
        beforeReward: (showAdFn) => {
          if (!current()) return;
          rewarded = true;
          this.showAd = showAdFn;
          this.slot = 'ready';
          this.armWatchdog(null); // a loaded ad may sit until the player dies
          this.emit('ad_ready');
        },
        adViewed: () => {
          if (current()) this.finish('viewed');
        },
        adDismissed: () => {
          if (current()) this.finish('dismissed');
        },
        adBreakDone: (info) => {
          // Fires for every outcome, including "there was no ad to show". If the
          // reward callback never ran, the placement simply failed — say so
          // once, then go back to a clean slate so the next run can try again.
          if (!current()) return;
          if (!rewarded) this.emit('ad_fail', { reason: info?.breakStatus ?? 'unfilled' });
          this.conclude('error');
        },
      });
    } catch (err) {
      this.emit('ad_fail', { reason: 'adbreak_threw', detail: describe(err) });
      this.conclude('error');
      return;
    }
    // A break that never fills and never reports back would otherwise leave the
    // slot occupied for the lifetime of the page — silently, and for every
    // later run.
    if (this.slot === 'preparing') {
      this.armWatchdog(() => {
        this.emit('ad_fail', { reason: 'prepare_timeout' });
        this.conclude('error');
      }, PREPARE_TIMEOUT_MS);
    }
  }

  /** Replace the pending watchdog (null clears it). */
  private armWatchdog(fn: (() => void) | null, ms = 0): void {
    this.cancelWatchdog?.();
    this.cancelWatchdog = fn ? this.schedule(fn, ms) : null;
  }

  /**
   * Show the loaded rewarded ad. Resolves once Google reports the outcome;
   * only `'viewed'` may grant a revive (design §8.1).
   */
  showRewarded(): Promise<RewardOutcome> {
    if (!this.rewardedReady() || !this.showAd) {
      this.emit('ad_fail', { reason: 'not_ready' });
      return Promise.resolve<RewardOutcome>('error');
    }
    const show = this.showAd;
    this.slot = 'showing';
    this.emit('ad_revive_shown');
    return new Promise<RewardOutcome>((resolve) => {
      this.settle = resolve;
      // The caller is a frozen death frame with no overlay left on it: if the
      // SDK never reports back, this promise MUST still settle or the run is
      // stranded with no way out but a reload.
      this.armWatchdog(() => {
        this.emit('ad_fail', { reason: 'timeout' });
        this.conclude('error');
      }, SHOW_TIMEOUT_MS);
      try {
        show();
      } catch (err) {
        this.emit('ad_fail', { reason: 'show_threw', detail: describe(err) });
        this.finish('error');
      }
    });
  }

  private finish(outcome: RewardOutcome): void {
    if (outcome === 'viewed') this.emit('ad_revive_viewed');
    else if (outcome === 'dismissed') this.emit('ad_revive_dismissed');
    this.conclude(outcome);
  }

  /**
   * The placement is over (viewed, dismissed or never filled): clean slate, and
   * settle any pending `showRewarded()`. A break that ends without ever
   * reporting an outcome still owes the caller an answer — otherwise the death
   * screen would wait on a promise that never resolves. Idempotent, because
   * `adBreakDone` normally arrives right after `adViewed`/`adDismissed`.
   */
  private conclude(outcome: RewardOutcome): void {
    const settle = this.settle;
    this.settle = null;
    this.showAd = null;
    this.slot = 'none';
    this.armWatchdog(null);
    // Retire this placement's generation: anything it reports from here on is a
    // straggler and must not touch the next run's ad.
    this.epoch++;
    settle?.(outcome);
  }
}

function describe(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// --- page-wide instance ----------------------------------------------------

let instance: AdManager | null = null;

/** Called once at boot with whatever the server said (design §8.2). */
export function initAds(config: AdsConfig, opts: AdManagerOptions = {}): AdManager {
  instance = new AdManager(config, opts);
  return instance;
}

/** The live manager; an inert disabled one before `initAds`. */
export function ads(): AdManager {
  return (instance ??= new AdManager(ADS_DISABLED));
}
